import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
// Import your giant Base64 strings from the file you created
import { poppinsBoldBase64, poppinsRegularBase64 } from "./Font";
import { calculateTotalRow } from '../Formulas/ReportFormulas';
import { inferColumnDecimals, looksLikeDecimalColumn } from '../HelperFunctions/ReportHelpers';


export const exportToPDF = (data, reportPayload, pickupDaysLabel, selectedRows, dateRange, displaySettings, showLegalName, totals) => {
    const doc = new jsPDF("l", "mm", "a2");

    // --- FONT REGISTRATION ---
    // Register Regular
    doc.addFileToVFS("Poppins-Regular.ttf", poppinsRegularBase64);
    doc.addFont("Poppins-Regular.ttf", "Poppins", "normal");

    // Register Bold
    doc.addFileToVFS("Poppins-Bold.ttf", poppinsBoldBase64);
    doc.addFont("Poppins-Bold.ttf", "Poppins", "bold");

    // Set default font for the whole document
    doc.setFont("Poppins", "normal");
    // -------------------------

    const isMultiple = selectedRows?.length > 1 || (reportPayload?.CorporationIDLst && String(reportPayload.CorporationIDLst).includes(','));

    const revenueKeys = [
        "OTBADR", "OTBRoomRev", "OTBRevPar",
        "PickUpADR", "PickUpRev",
        "BudgetAdr", "BudgetRoomRev", "BudgetRevPar",
        "ADRVariance", "RoomRevenueVariance",
        "LYADR", "LYRoomRev", "LYRevPar",
        "LYADRVariance", "LYRoomRevenueVariance"
    ];
    const occupancyKeys = [
        "OTBOcc", "PickUpOcc", "BudgetOcc", "OccVariance", "LYOccupancy", "LYOccVariance"
    ];

    const reportStructure = [
        {
            title: "Details",
            columns: isMultiple ? [
                { label: "Corp Name", key: "CorporationName", dataKey: showLegalName ? "CorpLegalName" : "Corpname" },
                { label: "Legal Name", key: "LegalName", dataKey: "PCName" },
                { label: "Brand Name", key: "BrandName", dataKey: "BrandName" },
                { label: "Total Rooms", key: "TotalRooms", dataKey: "TotRooms" },
                { label: "Trns Rooms", key: "TrnsRooms", dataKey: "TransRooms" },
                { label: "Grp Rooms", key: "GrpRooms", dataKey: "GrpRooms" },
            ] : [
                { label: "Date", key: "Date", dataKey: "SaleDate" },
                { label: "DOW", key: "DOW", dataKey: "Dayweek" },
                { label: "Total Rooms", key: "TotalRooms", dataKey: "TotRooms" },
                { label: "Trns Rooms", key: "TrnsRooms", dataKey: "TransRooms" },
                { label: "Grp Rooms", key: "GrpRooms", dataKey: "GrpRooms" },
            ]
        },
        {
            title: "OTB Actuals",
            columns: [
                { label: "Curr Rooms Sold", key: "OTBRoomsSold", dataKey: "CurrRoomsSold" },
                { label: "Curr Occ", key: "OTBOcc", dataKey: "CurrOcc" },
                { label: "Curr ADR", key: "OTBADR", dataKey: "CurrADR" },
                { label: "Curr Rev Par", key: "OTBRevPar", dataKey: "CurrRevPar" },
                { label: "Curr Room Rev", key: "OTBRoomRev", dataKey: "CurrRoomRev" },
            ]
        },
        {
            title: pickupDaysLabel ? `${pickupDaysLabel} Pickup` : "Pickup",
            columns: [
                { label: "Rooms", key: "PickUpRooms", dataKey: "PickUpRooms" },
                { label: "Occ", key: "PickUpOcc", dataKey: "PickUpOcc" },
                { label: "ADR", key: "PickUpADR", dataKey: "PickUpADR" },
                { label: "Rev", key: "PickUpRev", dataKey: "PickUpRoomRev" },
            ]
        },
        {
            title: "Budget",
            columns: [
                { label: "Bud Sold", key: "BudgetRoomsSold", dataKey: "BudRoomsSold" },
                { label: "Bud Occ %", key: "BudgetOcc", dataKey: "BudOcc" },
                { label: "Bud ADR", key: "BudgetAdr", dataKey: "BudADR" },
                { label: "Bud RevPAR", key: "BudgetRevPar", dataKey: "BudRevPar" },
                { label: "Bud Rm Rev", key: "BudgetRoomRev", dataKey: "BudRoomRev" },
            ]
        },
        {
            title: "Day Variance",
            columns: [
                { label: "Rms(Bud)", key: "RoomsSoldVariance", dataKey: "RoomsSoldVar" },
                { label: "Occ %(Bud)", key: "OccVariance", dataKey: "OccVar" },
                { label: "ADR(Bud)", key: "ADRVariance", dataKey: "ADRVar" },
                { label: "Rev(Bud)", key: "RoomRevenueVariance", dataKey: "RoomRevVar" },
            ]
        },
        {
            title: "LY Actuals",
            columns: [
                { label: "LY Sold", key: "LYRoomsSold", dataKey: "LYRoomsSold" },
                { label: "LY Occ%", key: "LYOccupancy", dataKey: "LYOcc" },
                { label: "LY ADR", key: "LYADR", dataKey: "LYADR" },
                { label: "LY RevPAR", key: "LYRevPar", dataKey: "LYRevPar" },
                { label: "LY Rm Rev", key: "LYRoomRev", dataKey: "LYRoomRev" },
            ]
        },
        {
            title: "Day Variance",
            columns: [
                { label: "Rms(LY)", key: "LYRoomsSoldVariance", dataKey: "LYRoomsSoldVar" },
                { label: "Occ %(LY)", key: "LYOccVariance", dataKey: "LYOccVar" },
                { label: "ADR(LY)", key: "LYADRVariance", dataKey: "LYADRVar" },
                { label: "Rev(LY)", key: "LYRoomRevenueVariance", dataKey: "LYRoomRevVar" },
            ]
        }
    ];

    const filteredStructure = (pickupDaysLabel && pickupDaysLabel.toLowerCase() === 'n/a') ? reportStructure.filter(s => !s.title.toLowerCase().includes('pickup')) : reportStructure;

    const visibleSections = filteredStructure.map(section => ({
        title: section.title,
        columns: section.columns.filter(col => {
            if (isMultiple && (col.key === 'CorporationName' || col.key === 'LegalName' || col.key === 'BrandName')) return true;
            return reportPayload && reportPayload[col.key];
        })
    })).filter(section => section.columns.length > 0);

    const formatText = (text) => {
        if (!text || typeof text !== 'string') return text;
        if (displaySettings?.textFormat === 'upper') return text.toUpperCase();
        if (displaySettings?.textFormat === 'lower') return text.toLowerCase();
        return text;
    };

    const headRow1 = [];
    const headRow2 = [];

    visibleSections.forEach(section => {
        headRow1.push({ content: formatText(section.title), colSpan: section.columns.length, styles: { halign: 'center' } });
        section.columns.forEach(col => {
            headRow2.push(formatText(col.label));
        });
    });

    let head = [headRow1, headRow2];

    let corpName = '';
    let pcName = '';

    if (selectedRows && selectedRows.length > 1) {
        corpName = 'Multiple';
        pcName = 'Multiple';
    } else if (data && data.length > 0) {
        corpName = (showLegalName ? data[0].CorpLegalName : data[0].Corpname) || data[0].Corpname || '';
        pcName = data[0].PCName || '';
    } else if (selectedRows && selectedRows.length === 1) {
        corpName = selectedRows[0].corpName || '';
    }

    if (corpName || pcName) {
        const totalCols = headRow2.length;
        const infoRow = [];
        if (totalCols > 0) {
            if (pcName && totalCols > 1) {
                const mid = Math.ceil(totalCols / 2);
                infoRow.push({
                    content: `${formatText("Corporation/Legal Name")} : ${formatText(corpName)}`,
                    colSpan: mid,
                    styles: { halign: 'left', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: 20 }
                });
                infoRow.push({
                    content: `${formatText("Profit Center")} : ${formatText(pcName)}`,
                    colSpan: totalCols - mid,
                    styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: 20 }
                });
            } else {
                infoRow.push({
                    content: `${formatText("Corporation/Legal Name")} : ${formatText(corpName)}`,
                    colSpan: totalCols,
                    styles: { halign: 'left', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: 20 }
                });
            }
            head.unshift(infoRow);
        }
    }

    let safeData = Array.isArray(data) ? data : [];
    if (displaySettings?.excludeZero) {
        const isNumericColumn = (key) => !['CorporationName', 'LegalName', 'BrandName', 'Date', 'DOW', 'PCName', 'CorpLegalName'].includes(key);
        const numericKeysToCheck = [];
        visibleSections.forEach(section => {
            section.columns.forEach(col => {
                if (isNumericColumn(col.key)) {
                    numericKeysToCheck.push(col.dataKey);
                }
            });
        });

        if (numericKeysToCheck.length > 0) {
            safeData = safeData.filter(row => {
                return numericKeysToCheck.some(key => {
                    const val = parseFloat(row[key]);
                    return !isNaN(val) && Math.abs(val) > 0.000001;
                });
            });
        }
    }

    // Total Row Calculations (logic preserved from your snippet)
    const calculatedTotal = calculateTotalRow(safeData, totals) || {};
    const totalRow = {
        SaleDate: 'Total',
        Corpname: 'Total',
        PCName: '',
        BrandName: '',
        Dayweek: '',
        ...calculatedTotal
    };

    const pickupDataKeys = ["PickUpRooms", "PickUpOcc", "PickUpADR", "PickUpRoomRev"];
    if (safeData.length > 0) {
        pickupDataKeys.forEach(key => {
            if (safeData.every(row => row[key] === 'N/A')) {
                totalRow[key] = 'N/A';
            }
        });
    }

    const columnDecimals = inferColumnDecimals(safeData);

    const getColDecimals = (col) => {
        if (displaySettings?.withoutCents) return 0;
        if (col?.dataKey && columnDecimals[col.dataKey] !== undefined) return columnDecimals[col.dataKey];
        return (looksLikeDecimalColumn(col?.dataKey) || looksLikeDecimalColumn(col?.key)) ? 2 : 0;
    };

    const dataWithTotal = [...safeData, totalRow];

    const body = dataWithTotal.map(row => {
        const rowValues = [];
        const isTotalRow = row.SaleDate === 'Total' || row.Corpname === 'Total';

        visibleSections.forEach(section => {
            section.columns.forEach(col => {
                let val = row[col.dataKey];
                let cellStyles = { font: "Poppins" }; // Explicit font per cell

                if (isTotalRow) {
                    cellStyles.fontStyle = 'bold';
                }

                if (col.key === 'BrandName' && !val && selectedRows) {
                    const matchedRow = selectedRows.find(r => r.corpName === row.Corpname);
                    if (matchedRow) val = matchedRow.brand;
                }

                if (col.key === 'Date' && val && val !== 'Total') {
                    val = dayjs(val).format('MM/DD/YYYY');
                }

                const isNumeric = !['CorporationName', 'LegalName', 'BrandName', 'Date', 'DOW', 'PCName', 'CorpLegalName'].includes(col.key);

                if (isNumeric) {
                    if (val !== null && val !== undefined && val !== '') {
                        let num = Number(val);
                        if (!isNaN(num)) {
                            const decimals = getColDecimals(col);
                            let formattedNum;
                            if (displaySettings?.withoutCents) {
                                formattedNum = Math.trunc(Math.abs(num)).toString();
                            } else {
                                formattedNum = decimals > 0 ? Math.abs(num).toFixed(decimals) : Math.trunc(Math.abs(num)).toString();
                            }
                            formattedNum = formattedNum.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                            if (num < 0) {
                                if (displaySettings?.showRedNegative) cellStyles.textColor = [255, 0, 0];
                                if (displaySettings?.negativeFormat === 'parentheses') val = `(${formattedNum})`;
                                else if (displaySettings?.negativeFormat === 'trailing') val = `${formattedNum}-`;
                                else val = `-${formattedNum}`;
                            } else {
                                val = formattedNum;
                            }

                            if (revenueKeys.includes(col.key)) {
                                val = `$${val}`;
                            } else if (occupancyKeys.includes(col.key)) {
                                val = `${val}%`;
                            }
                        }
                    }
                } else {
                    if (val && typeof val === 'string') {
                        if (displaySettings?.textFormat === 'upper') val = val.toUpperCase();
                        if (displaySettings?.textFormat === 'lower') val = val.toLowerCase();
                    }
                }

                rowValues.push({ content: val ?? '', styles: cellStyles });
            });
        });
        return rowValues;
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const totalColumns = headRow2.length;
    const colWidth = totalColumns > 0 ? (pageWidth - (margin * 2)) / totalColumns : 'auto';

    let headerY = 27;
    if (pickupDaysLabel && pickupDaysLabel.toLowerCase() !== 'n/a') {
        headerY += 5;
    }
    const marginTop = headerY + 3;

    autoTable(doc, {
        startY: marginTop,
        margin: { top: marginTop, left: margin, right: margin },
        head: head,
        body: body,
        theme: 'grid',
        styles: {
            font: "Poppins",
            fontSize: 7,
            cellPadding: 1,
            halign: 'center',
            cellWidth: colWidth,
            overflow: 'linebreak'
        },
        headStyles: {
            fillColor: [220, 220, 220],
            textColor: 20,
            lineColor: 200,
            lineWidth: 0.1,
            font: "Poppins",
            fontStyle: 'bold'
        },
        didDrawPage: (data) => {
            doc.setFontSize(14);
            doc.setFont("Poppins", "bold");
            doc.text("OTB Report", pageWidth / 2, 15, { align: "center" });

            doc.setFontSize(9);
            doc.setFont("Poppins", "normal");
            const fromDate = dateRange?.[0] ? dayjs(dateRange[0]).format('MMMM DD, YYYY') : '';
            const toDate = dateRange?.[1] ? dayjs(dateRange[1]).format('MMMM DD, YYYY') : '';
            doc.text(`${fromDate} To ${toDate}`, pageWidth / 2, 22, { align: "center" });

            let currentY = 27;
            if (pickupDaysLabel && pickupDaysLabel.toLowerCase() !== 'n/a') {
                doc.text(`${pickupDaysLabel} Pickup`, pageWidth / 2, currentY, { align: "center" });
                currentY += 5;
            }
        }
    });

    doc.save("OTB_Report.pdf");
};